import React, { useState, useEffect } from 'react';
import { Alert, Button, ButtonGroup, Card, Spinner } from 'react-bootstrap';
import BuilderCanvas from './Canvas/BuilderCanvas';
import ElementToolbar from './Toolbar/ElementToolbar';
import PropertyPanel from './Toolbar/PropertyPanel';
import './styles.css';

const LetterheadBuilderIndex = () => {
    // Get letterheadId from data attribute
    const letterheadId = document.getElementById('letterhead-builder-root')?.dataset?.letterheadId;
    const [elements, setElements] = useState([]);
    const [selectedElement, setSelectedElement] = useState(null);
    const [canvasSettings, setCanvasSettings] = useState({
        width: 210,
        height: 297,
        unit: 'mm',
        backgroundColor: '#ffffff',
        gridSize: 5,
        showGrid: true,
        snapToGrid: true
    });
    const [pageType, setPageType] = useState('first');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [builderMode, setBuilderMode] = useState(false);

    useEffect(() => {
        loadLetterhead();
    }, [letterheadId]);

    const loadLetterhead = async () => {
        try {
            setLoading(true);
            const formData = new FormData();
            formData.append('id', letterheadId);

            const response = await fetch('/index.php/settings/letterhead_builder_load', {
                method: 'POST',
                body: formData
            });

            const data = await response.json();

            if (data.success) {
                setElements(data.elements || []);
                setCanvasSettings(data.canvasSettings || canvasSettings);
                setBuilderMode(data.builderMode || false);
            } else {
                setMessage({ type: 'danger', text: 'Fehler beim Laden: ' + data.error });
            }
        } catch (error) {
            setMessage({ type: 'danger', text: 'Fehler beim Laden: ' + error.message });
        } finally {
            setLoading(false);
        }
    };

    const saveLetterhead = async () => {
        try {
            setSaving(true);

            const payload = {
                elements,
                canvasSettings,
                builderMode: true
            };

            const response = await fetch(`/index.php/settings/letterhead_builder_save`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    id: letterheadId,
                    ...payload
                })
            });

            const data = await response.json();

            if (data.success) {
                setMessage({ type: 'success', text: 'Briefkopf erfolgreich gespeichert!' });
                setTimeout(() => setMessage(null), 3000);
            } else {
                setMessage({ type: 'danger', text: 'Fehler beim Speichern: ' + data.error });
            }
        } catch (error) {
            setMessage({ type: 'danger', text: 'Fehler beim Speichern: ' + error.message });
        } finally {
            setSaving(false);
        }
    };

    const addElement = (type) => {
        const newElement = {
            element_id: 'temp_' + Date.now(),
            element_type: type,
            x_position: 20,
            y_position: 20,
            width: type === 'line' ? 170 : 50,
            height: type === 'line' ? 2 : 30,
            font_family: 'Arial',
            font_size: 12,
            font_color: '#000000',
            font_weight: 'normal',
            text_align: 'left',
            background_color: type === 'rectangle' ? '#f0f0f0' : null,
            border_color: null,
            border_width: 0,
            text_content: type === 'text' ? 'Neuer Text' : (type === 'address' ? '{{photographer.name}}\n{{photographer.address}}' : ''),
            image_path: null,
            z_index: elements.length,
            rotation: 0,
            opacity: 1.0,
            page_type: pageType,
            properties: {}
        };

        setElements([...elements, newElement]);
        setSelectedElement(newElement);
    };

    const updateElement = (updatedElement) => {
        setElements(elements.map(el =>
            el.element_id === updatedElement.element_id ? updatedElement : el
        ));
        if (selectedElement && selectedElement.element_id === updatedElement.element_id) {
            setSelectedElement(updatedElement);
        }
    };

    const deleteElement = (elementId) => {
        setElements(elements.filter(el => el.element_id !== elementId));
        if (selectedElement && selectedElement.element_id === elementId) {
            setSelectedElement(null);
        }
    };

    const duplicateElement = (element) => {
        const newElement = {
            ...element,
            element_id: 'temp_' + Date.now(),
            x_position: element.x_position + 10,
            y_position: element.y_position + 10,
            z_index: elements.length
        };
        setElements([...elements, newElement]);
    };

    const getFilteredElements = () => {
        return elements.filter(el => el.page_type === pageType || el.page_type === 'all');
    };

    if (loading) {
        return (
            <div className="text-center p-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Lädt...</span>
                </Spinner>
            </div>
        );
    }

    return (
        <div className="letterhead-builder">
            <Card>
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h5 className="mb-0">Briefkopf Builder</h5>
                            <small className="text-muted">
                                Visueller Editor für Briefköpfe - Experimentell
                            </small>
                        </div>
                        <div className="d-flex gap-2 align-items-center">
                            <ButtonGroup>
                                <Button
                                    size="sm"
                                    variant={pageType === 'first' ? 'primary' : 'outline-primary'}
                                    onClick={() => setPageType('first')}
                                >
                                    1. Seite
                                </Button>
                                <Button
                                    size="sm"
                                    variant={pageType === 'following' ? 'primary' : 'outline-primary'}
                                    onClick={() => setPageType('following')}
                                >
                                    Folgeseiten
                                </Button>
                            </ButtonGroup>
                            <Button
                                variant="success"
                                size="sm"
                                onClick={saveLetterhead}
                                disabled={saving}
                            >
                                {saving ? 'Speichere...' : 'Speichern'}
                            </Button>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body>
                    {message && (
                        <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
                            {message.text}
                        </Alert>
                    )}

                    <div className="builder-layout">
                        <div className="builder-sidebar-left">
                            <ElementToolbar onAddElement={addElement} />
                        </div>

                        <div className="builder-canvas-container">
                            <BuilderCanvas
                                elements={getFilteredElements()}
                                selectedElement={selectedElement}
                                onSelectElement={setSelectedElement}
                                onUpdateElement={updateElement}
                                canvasSettings={canvasSettings}
                                pageType={pageType}
                            />
                        </div>

                        <div className="builder-sidebar-right">
                            <PropertyPanel
                                element={selectedElement}
                                onUpdateProperty={updateElement}
                                onDelete={deleteElement}
                                onDuplicate={duplicateElement}
                            />
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

export default LetterheadBuilderIndex;
